import { useEffect, useState, type FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/shadcn";
import { AppButton, AppInput, FigmaTypography } from "@libs/zapi-ui/custom";
import { useTranslation } from "react-i18next";

type TProps = {
  isOpen: boolean;
  title: string;
  confirmText: string;
  initialName?: string;
  isSubmitting?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onConfirm: (name: string) => void;
};

export function NamePromptModal({
  isOpen,
  title,
  confirmText,
  initialName = "",
  isSubmitting,
  errorMessage,
  onClose,
  onConfirm
}: TProps) {
  const { t } = useTranslation(["projects", "common"]);
  const [name, setName] = useState(initialName);

  useEffect(() => {
    if (isOpen) setName(initialName);
  }, [isOpen, initialName]);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !isSubmitting;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm(trimmed);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent onClick={(e) => e.stopPropagation()} className="w-[420px]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              <FigmaTypography variant="heading-3" color="zinc900">
                {title}
              </FigmaTypography>
            </DialogTitle>
          </DialogHeader>
          <AppInput
            label={t("projects:savedFilters.nameLabel")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("projects:savedFilters.namePlaceholder")}
            inputError={errorMessage ?? undefined}
            maxLength={255}
            fullWidth
          />
          <DialogFooter>
            <AppButton type="button" appearance="outlined" color="zinc150" onClick={onClose}>
              {t("common:cancel")}
            </AppButton>
            <AppButton type="submit" color="zinc950" disabled={!canSubmit}>
              {confirmText}
            </AppButton>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
